import type { ReactElement } from 'react';
import type { MaterialItem, MaterialLineKind } from '../../domain/types';
import {
  LINE_PIPE_END_FINISH_OPTIONS,
  LINE_PIPE_MFG_OPTIONS,
  LINE_PIPE_WALL_THICKNESS_OPTIONS
} from '../../domain/linePipeFormOptions';
import { INCH_FRACTION_SELECT_OPTIONS, type InchFraction } from '../../domain/casingInches';
import { usesStructuredEquipmentForm } from '../../domain/procurementRequest';

type PatchFn = (patch: Partial<MaterialItem>) => void;

export interface MaterialStructuredSpecFieldsProps {
  m: MaterialItem;
  onPatch: PatchFn;
  invalidClass: string;
}

export function MaterialStructuredSpecFields({
  m,
  onPatch,
  invalidClass
}: MaterialStructuredSpecFieldsProps): ReactElement | null {
  if (!usesStructuredEquipmentForm(m.kind as MaterialLineKind)) {
    return null;
  }

  const kind = m.kind;
  if (kind === 'Transformers' && m.transformer) {
    const t = m.transformer;
    return (
      <div
        className={`material-spec-grid grid ${invalidClass}`.trim()}
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(14rem, 1fr))' }}
      >
        <label>
          Voltage rating
          <input value={t.voltageRating} onChange={(e) => onPatch({ transformer: { ...t, voltageRating: e.target.value } })} />
        </label>
        <label>
          Capacity
          <input value={t.capacity} onChange={(e) => onPatch({ transformer: { ...t, capacity: e.target.value } })} />
        </label>
        <label>
          Cooling
          <select value={t.cooling ?? ''} onChange={(e) => onPatch({ transformer: { ...t, cooling: (e.target.value as 'ONAN' | 'ONAF' | '') || undefined } })}>
            <option value="">…</option>
            <option value="ONAN">ONAN</option>
            <option value="ONAF">ONAF</option>
          </select>
        </label>
        <label>
          Insulation class
          <input value={t.insulationClass} onChange={(e) => onPatch({ transformer: { ...t, insulationClass: e.target.value } })} />
        </label>
        <label>
          Impedance (%)
          <input
            type="number"
            step="0.01"
            value={t.impedancePercent ?? ''}
            onChange={(e) => onPatch({ transformer: { ...t, impedancePercent: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Environmental protection
          <input
            value={t.environmentalProtection}
            onChange={(e) => onPatch({ transformer: { ...t, environmentalProtection: e.target.value } })}
          />
        </label>
        <label>
          Oil type
          <input value={t.oilType} onChange={(e) => onPatch({ transformer: { ...t, oilType: e.target.value } })} />
        </label>
      </div>
    );
  }

  if (kind === 'ElectricMotors' && m.electricMotor) {
    const e = m.electricMotor;
    return (
      <div
        className={`material-spec-grid grid ${invalidClass}`.trim()}
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
      >
        <label>
          Power (HP)
          <input
            type="number"
            step="0.01"
            value={e.powerOutputHp ?? ''}
            onChange={(ev) => onPatch({ electricMotor: { ...e, powerOutputHp: ev.target.value === '' ? undefined : Number(ev.target.value) } })}
            title="Nameplate"
          />
        </label>
        <label>
          Torque design
          <input
            value={e.torqueDesign}
            placeholder="e.g. NEMA Design D for rod pumping"
            onChange={(ev) => onPatch({ electricMotor: { ...e, torqueDesign: ev.target.value } })}
          />
        </label>
        <label>
          Speed (RPM)
          <input
            type="number"
            value={e.speedRpm ?? ''}
            onChange={(ev) => onPatch({ electricMotor: { ...e, speedRpm: ev.target.value === '' ? undefined : Number(ev.target.value) } })}
          />
        </label>
        <label>
          Voltage (V)
          <input
            type="number"
            value={e.voltage ?? ''}
            onChange={(ev) => onPatch({ electricMotor: { ...e, voltage: ev.target.value === '' ? undefined : Number(ev.target.value) } })}
          />
        </label>
        <label>
          Enclosure
          <select
            value={e.enclosure ?? ''}
            onChange={(ev) => onPatch({ electricMotor: { ...e, enclosure: ev.target.value ? (ev.target.value as 'TEFC' | 'ODP') : undefined } })}
          >
            <option value="">…</option>
            <option value="TEFC">TEFC</option>
            <option value="ODP">ODP</option>
          </select>
        </label>
        <label>
          Duty
          <input
            value={e.duty}
            placeholder="e.g. Continuous duty, 1.15 service factor"
            onChange={(ev) => onPatch({ electricMotor: { ...e, duty: ev.target.value } })}
          />
        </label>
      </div>
    );
  }

  if (kind === 'PumpingUnits' && m.pumpingUnit) {
    const p = m.pumpingUnit;
    return (
      <div className="material-spec-grid grid" style={{ display: 'grid', gap: '0.75rem' }}>
        <label>
          Type
          <select
            value={p.unitType}
            onChange={(e) => {
              const v = e.target.value as 'BeamPumping' | 'HydraulicPumping';
              onPatch({ pumpingUnit: { ...p, unitType: v } });
            }}
          >
            <option value="BeamPumping">Beam (pumpjack)</option>
            <option value="HydraulicPumping">Hydraulic (HPU)</option>
          </select>
        </label>
        {p.unitType === 'BeamPumping' && p.beam && (
          <div
            className="material-spec-grid grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
          >
            <label>
              API / manufacturer specifications
              <input
                value={p.beam.apiSpecifications}
                onChange={(e) => onPatch({ pumpingUnit: { ...p, beam: { ...p.beam!, apiSpecifications: e.target.value } } })}
              />
            </label>
            <label>
              Load rating (lb)
              <input
                type="number"
                value={p.beam.loadRatingLbs ?? ''}
                onChange={(e) =>
                  onPatch({
                    pumpingUnit: { ...p, beam: { ...p.beam!, loadRatingLbs: e.target.value === '' ? undefined : Number(e.target.value) } }
                  })
                }
              />
            </label>
            <label>
              Stroke (ft)
              <input
                type="number"
                step="0.01"
                value={p.beam.strokeLengthFeet ?? ''}
                onChange={(e) =>
                  onPatch({
                    pumpingUnit: { ...p, beam: { ...p.beam!, strokeLengthFeet: e.target.value === '' ? undefined : Number(e.target.value) } }
                  })
                }
              />
            </label>
            <label>
              Stroke (in.) whole + fraction
              <div className="inch-input-row">
                <input
                  value={p.beam.strokeInchesWhole}
                  onChange={(e) => onPatch({ pumpingUnit: { ...p, beam: { ...p.beam!, strokeInchesWhole: e.target.value } } })}
                />
                <select
                  value={p.beam.strokeInchesFraction}
                  onChange={(e) =>
                    onPatch({
                      pumpingUnit: { ...p, beam: { ...p.beam!, strokeInchesFraction: e.target.value as InchFraction } }
                    })
                  }
                >
                  {INCH_FRACTION_SELECT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="inch-fraction-hint">in</span>
              </div>
            </label>
            <label>
              Counterbalance
              <select
                value={p.beam.counterbalance ?? ''}
                onChange={(e) =>
                  onPatch({
                    pumpingUnit: { ...p, beam: { ...p.beam!, counterbalance: e.target.value as 'Counterbalanced' | 'AirBalanced' } }
                  })
                }
              >
                <option value="">…</option>
                <option value="Counterbalanced">Counterbalanced</option>
                <option value="AirBalanced">Air balanced</option>
              </select>
            </label>
            <label>
              Prime mover
              <select
                value={p.beam.primeMover ?? ''}
                onChange={(e) =>
                  onPatch({
                    pumpingUnit: { ...p, beam: { ...p.beam!, primeMover: e.target.value as 'AcElectricMotor' | 'InternalCombustionEngine' } }
                  })
                }
              >
                <option value="">…</option>
                <option value="AcElectricMotor">AC electric motor</option>
                <option value="InternalCombustionEngine">ICE</option>
              </select>
            </label>
            <label>
              Operating speed (SPM)
              <input
                type="number"
                step="0.01"
                value={p.beam.operatingStrokesPerMinute ?? ''}
                onChange={(e) =>
                  onPatch({
                    pumpingUnit: { ...p, beam: { ...p.beam!, operatingStrokesPerMinute: e.target.value === '' ? undefined : Number(e.target.value) } }
                  })
                }
              />
            </label>
          </div>
        )}
        {p.unitType === 'HydraulicPumping' && p.hydraulic && (
          <div
            className="material-spec-grid grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
          >
            <label>
              Stroke (ft)
              <input
                type="number"
                step="0.01"
                value={p.hydraulic.strokeFeet ?? ''}
                onChange={(e) =>
                  onPatch({
                    pumpingUnit: { ...p, hydraulic: { ...p.hydraulic!, strokeFeet: e.target.value === '' ? undefined : Number(e.target.value) } }
                  })
                }
              />
            </label>
            <label>
              Stroke (in.) whole + fraction
              <div className="inch-input-row">
                <input
                  value={p.hydraulic.strokeInchesWhole}
                  onChange={(e) => onPatch({ pumpingUnit: { ...p, hydraulic: { ...p.hydraulic!, strokeInchesWhole: e.target.value } } })}
                />
                <select
                  value={p.hydraulic.strokeInchesFraction}
                  onChange={(e) =>
                    onPatch({
                      pumpingUnit: { ...p, hydraulic: { ...p.hydraulic!, strokeInchesFraction: e.target.value as InchFraction } }
                    })
                  }
                >
                  {INCH_FRACTION_SELECT_OPTIONS.map((opt) => (
                    <option key={`h-${opt.value}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </label>
            <label>
              Peak load (lbm)
              <input
                type="number"
                value={p.hydraulic.peakLoadLbm ?? ''}
                onChange={(e) =>
                  onPatch({
                    pumpingUnit: { ...p, hydraulic: { ...p.hydraulic!, peakLoadLbm: e.target.value === '' ? undefined : Number(e.target.value) } }
                  })
                }
              />
            </label>
          </div>
        )}
      </div>
    );
  }

  if (kind === 'LinePipe' && m.linePipe) {
    const l = m.linePipe;
    return (
      <div
        className="material-spec-grid grid"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
      >
        <label>
          Diameter
          <input value={l.diameter} onChange={(e) => onPatch({ linePipe: { ...l, diameter: e.target.value } })} />
        </label>
        <label>
          Wall thickness
          <select
            value={l.wallThickness}
            onChange={(e) => onPatch({ linePipe: { ...l, wallThickness: e.target.value } })}
          >
            <option value="">…</option>
            {LINE_PIPE_WALL_THICKNESS_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Length (ft)
          <input
            type="number"
            step="0.01"
            value={l.lengthFeet ?? ''}
            onChange={(e) => onPatch({ linePipe: { ...l, lengthFeet: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Length (in.) whole + fraction
          <div className="inch-input-row">
            <input value={l.lengthInchesWhole} onChange={(e) => onPatch({ linePipe: { ...l, lengthInchesWhole: e.target.value } })} />
            <select
              value={l.lengthInchesFraction}
              onChange={(e) => onPatch({ linePipe: { ...l, lengthInchesFraction: e.target.value as InchFraction } })}
            >
              {INCH_FRACTION_SELECT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="inch-fraction-hint">in</span>
          </div>
        </label>
        <label>
          End finishes
          <select
            value={l.endFinishes}
            onChange={(e) => onPatch({ linePipe: { ...l, endFinishes: e.target.value } })}
          >
            <option value="">…</option>
            {LINE_PIPE_END_FINISH_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Mfg. process
          <select
            value={l.manufacturingProcess}
            onChange={(e) => onPatch({ linePipe: { ...l, manufacturingProcess: e.target.value } })}
          >
            <option value="">…</option>
            {LINE_PIPE_MFG_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Grade
          <input value={l.grade} onChange={(e) => onPatch({ linePipe: { ...l, grade: e.target.value } })} />
        </label>
      </div>
    );
  }

  if (kind === 'StorageTank' && m.storageTank) {
    const s = m.storageTank;
    return (
      <div
        className="material-spec-grid grid"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
      >
        <label>
          Standard
          <select
            value={s.standard ?? ''}
            onChange={(e) =>
              onPatch({
                storageTank: { ...s, standard: (e.target.value as 'ShopWeldedApi12F' | 'FieldErectedApi650' | '') || undefined }
              })
            }
          >
            <option value="">…</option>
            <option value="ShopWeldedApi12F">Shop welded (API 12F)</option>
            <option value="FieldErectedApi650">Field erected (API 650)</option>
          </select>
        </label>
        <label>
          Capacity (bbl)
          <input
            type="number"
            value={s.capacityBbl ?? ''}
            onChange={(e) => onPatch({ storageTank: { ...s, capacityBbl: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Operating pressure (psig)
          <input
            type="number"
            value={s.operatingPressurePsig ?? ''}
            onChange={(e) => onPatch({ storageTank: { ...s, operatingPressurePsig: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Materials
          <input
            value={s.materials}
            placeholder="e.g. Standard carbon steel SA-36"
            onChange={(e) => onPatch({ storageTank: { ...s, materials: e.target.value } })}
          />
        </label>
      </div>
    );
  }

  if (kind === 'PressureVessel' && m.pressureVessel) {
    const p = m.pressureVessel;
    return (
      <div className="material-spec-grid grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <label>
          Design standards
          <input value={p.standards} onChange={(e) => onPatch({ pressureVessel: { ...p, standards: e.target.value } })} />
        </label>
        <label>
          Pressure rating (psig)
          <input
            type="number"
            value={p.pressureRatingPsig ?? ''}
            onChange={(e) => onPatch({ pressureVessel: { ...p, pressureRatingPsig: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
      </div>
    );
  }

  if (kind === 'KnockOut' && m.knockOut) {
    const k = m.knockOut;
    return (
      <div
        className="material-spec-grid grid"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
      >
        <label>
          Type
          <select
            value={k.drumType ?? ''}
            onChange={(e) =>
              onPatch({ knockOut: { ...k, drumType: (e.target.value as 'FreeWaterKnockOut' | 'GasKnockOutDrum' | '') || undefined } })
            }
          >
            <option value="">…</option>
            <option value="FreeWaterKnockOut">Free water (FWKO)</option>
            <option value="GasKnockOutDrum">Gas knock-out</option>
          </select>
        </label>
        <label>
          Retention time (min)
          <input
            type="number"
            value={k.retentionTimeMinutes ?? ''}
            onChange={(e) => onPatch({ knockOut: { ...k, retentionTimeMinutes: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Size / note
          <input
            value={k.sizes}
            placeholder="e.g. 24 x 10 ft"
            onChange={(e) => onPatch({ knockOut: { ...k, sizes: e.target.value } })}
          />
        </label>
      </div>
    );
  }

  if (kind === 'EspCables' && m.espCable) {
    const c = m.espCable;
    return (
      <div
        className="material-spec-grid grid"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
      >
        <label>
          Voltage (kV)
          <input
            type="number"
            step="0.01"
            value={c.voltageKv ?? ''}
            onChange={(e) => onPatch({ espCable: { ...c, voltageKv: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Temp (°F)
          <input
            type="number"
            value={c.temperatureFahrenheit ?? ''}
            onChange={(e) => onPatch({ espCable: { ...c, temperatureFahrenheit: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Conductor sizes
          <input value={c.conductorSizes} onChange={(e) => onPatch({ espCable: { ...c, conductorSizes: e.target.value } })} />
        </label>
        <label>
          Cable geometry
          <input value={c.cableGeometry} placeholder="Flat or round" onChange={(e) => onPatch({ espCable: { ...c, cableGeometry: e.target.value } })} />
        </label>
        <label>
          Conductors
          <input value={c.conductors} onChange={(e) => onPatch({ espCable: { ...c, conductors: e.target.value } })} />
        </label>
        <label>
          Insulation
          <input value={c.insulation} onChange={(e) => onPatch({ espCable: { ...c, insulation: e.target.value } })} />
        </label>
        <label>
          Barrier / sheath
          <input value={c.barrierOrSheath} onChange={(e) => onPatch({ espCable: { ...c, barrierOrSheath: e.target.value } })} />
        </label>
        <label>
          Jacket
          <input value={c.jacket} onChange={(e) => onPatch({ espCable: { ...c, jacket: e.target.value } })} />
        </label>
        <label>
          Armor
          <input value={c.armor} onChange={(e) => onPatch({ espCable: { ...c, armor: e.target.value } })} />
        </label>
      </div>
    );
  }

  if (kind === 'Flares' && m.flare) {
    const f = m.flare;
    return (
      <div
        className="material-spec-grid grid"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}
      >
        <label>
          Standards
          <input value={f.standards} onChange={(e) => onPatch({ flare: { ...f, standards: e.target.value } })} />
        </label>
        <label>
          Destruction eff. (%)
          <input
            type="number"
            value={f.destructionEfficiencyPercent ?? ''}
            onChange={(e) => onPatch({ flare: { ...f, destructionEfficiencyPercent: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          NHV (Btu/scf)
          <input
            type="number"
            value={f.netHeatingValueBtuScf ?? ''}
            onChange={(e) => onPatch({ flare: { ...f, netHeatingValueBtuScf: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Tip velocity (ft/s)
          <input
            type="number"
            value={f.tipVelocityFps ?? ''}
            onChange={(e) => onPatch({ flare: { ...f, tipVelocityFps: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Visible emissions
          <input value={f.visibleEmissions} placeholder="e.g. 5 minutes" onChange={(e) => onPatch({ flare: { ...f, visibleEmissions: e.target.value } })} />
        </label>
        <label>
          Pilot
          <select
            value={f.pilot === undefined ? '' : f.pilot ? 'yes' : 'no'}
            onChange={(e) => onPatch({ flare: { ...f, pilot: e.target.value === '' ? undefined : e.target.value === 'yes' } })}
          >
            <option value="">…</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>
      </div>
    );
  }

  if (kind === 'Combustor' && m.combustor) {
    const c = m.combustor;
    return (
      <div className="material-spec-grid grid" style={{ gridTemplateColumns: '1fr' }}>
        <label>
          Performance (%)
          <input
            type="number"
            value={c.performancePercent ?? ''}
            onChange={(e) => onPatch({ combustor: { ...c, performancePercent: e.target.value === '' ? undefined : Number(e.target.value) } })}
          />
        </label>
        <label>
          Flow limits
          <input value={c.flowLimits} onChange={(e) => onPatch({ combustor: { ...c, flowLimits: e.target.value } })} />
        </label>
      </div>
    );
  }

  return <p className="hint">Select a material type to show specification fields.</p>;
}
